import {
  Box,
  Card,
  CardContent,
  CssBaseline,
  Divider,
  Menu,
  MenuItem,
  Popover,
  Typography,
} from "@mui/material";

import { ThemeProvider, createTheme } from "@mui/material/styles";

import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCurrentProfile } from "../../ContextWrapper";
import Sidebar from "./sideNav";

const base = createTheme();

const theme = createTheme({
  ...base,
  typography: {
    ...base.typography,
    fontFamily: "'lato', 'Inter', sans-serif",
  },
  palette: {
    ...base.palette,
    background: {
      ...base.palette.background,
      default: "#F4F6FF",
    },
    text: {
      ...base.palette.text,
      primary: "#111827",
      secondary: "#6B7280",
    },
    primary: {
      ...base.palette.primary,
      main: "#536DEC",
      dark: "#4C63D7",
    },
  },
  components: {
    ...base.components,
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
  },
});

export default function ClientDashboardLayout({
  children,
  title,
  contentMaxWidth = "auto",
  contentAlign = "center",
  header = false
}) {
  const navigate = useNavigate();
  const { profile, fireToast } = useCurrentProfile();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const isMobile = useMediaQuery("(max-width: 1280px)", { noSsr: true });

  useEffect(() => {
    const stored = localStorage.getItem("sidebarExpanded");
    if (stored !== null) {
      setSidebarExpanded(stored === "true");
    }
    const handleExpand = () => setSidebarExpanded(true);
    const handleCollapse = () => setSidebarExpanded(false);
    window.addEventListener("sidebar:expand", handleExpand);
    window.addEventListener("sidebar:collapse", handleCollapse);
    return () => {
      window.removeEventListener("sidebar:expand", handleExpand);
      window.removeEventListener("sidebar:collapse", handleCollapse);
    };
  }, []);

  const handleCustomerChanged = React.useCallback(() => {
    const stored = sessionStorage.getItem("selectedCustomerId");
    if (!stored) return;

    const customers = profile?.customers;
    if (!customers || customers.length === 0) return;

    const customer = customers.find((c) => c.id.toString() === stored);

    if (!customer) return;

    const needsOnboarding =
      customer.customer_type === "trial" &&
      profile.type === "client" &&
      !customer.is_demo &&
      customer.skipped_first_time_setup === false &&
      customer.completed_first_time_setup === false;

    if (needsOnboarding && window.location.pathname !== "/onboarding") {
      localStorage.setItem("customerOnboardingId", stored);
      window.dispatchEvent(new Event("customerOnboardingId-changed"));
      navigate("/onboarding");
    }
  }, [profile?.customers, navigate]);

  useEffect(() => {
    window.addEventListener(
      "selectedCustomerId-changed",
      handleCustomerChanged
    );

    return () => {
      window.removeEventListener(
        "selectedCustomerId-changed",
        handleCustomerChanged
      );
    };
  }, [handleCustomerChanged]);

  useEffect(() => {
    handleCustomerChanged();
  }, [profile?.customers, handleCustomerChanged]);

  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#F9FAFB" }}>
        <Sidebar
          expanded={sidebarExpanded}
          onToggle={setSidebarExpanded}
          customers={profile?.customers}
          profile={profile}
        />

        <Box
          sx={{
            flexGrow: 1,
            ml: isMobile ? 0 : sidebarExpanded ? "300px" : "95px",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {header && (

            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderBottom: "1px solid #E2E6EF",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: { xs: 2, sm: 3 },
                py: 2,
                position: "sticky",
                top: 0,
                zIndex: 1100,
                height: "71px",
                boxShadow: "0 4px 10px rgba(15,23,42,0.06)",
              }}
            ></Box>
        )}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 0,
              width: "100%",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                maxWidth: contentMaxWidth,
                width: "100%",
                ...(contentAlign === "left"
                  ? { mr: "auto" }
                  : contentAlign === "right"
                  ? { ml: "auto" }
                  : { mx: "auto" }),
              }}
            >
              {title && (
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, mb: 2, color: "#111827" }}
                >
                  {title}
                </Typography>
              )}

              {children}
            </Box>
          </Box>
        </Box>

        {/* Notifications */}
        <Popover
          open={Boolean(notificationAnchorEl)}
          anchorEl={notificationAnchorEl}
          onClose={() => setNotificationAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ mt: 1 }}
        >
          <Card sx={{ minWidth: 300 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" align="center">
                No new notifications
              </Typography>
            </CardContent>
          </Card>
        </Popover>

        {/* Profile Menu */}
        <Menu
          anchorEl={profileMenuAnchorEl}
          open={Boolean(profileMenuAnchorEl)}
          onClose={() => setProfileMenuAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ mt: 1 }}
        >
          <MenuItem
            onClick={() => {
              setProfileMenuAnchorEl(null);
              fireToast(
                "Profile Settings",
                "Under construction, coming soon....",
                "info"
              );
            }}
          >
            Profile Settings
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={() => {
              setProfileMenuAnchorEl(null);
              navigate("/logout");
            }}
          >
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </ThemeProvider>
  );
}
